import React, { Component } from 'react';
import { Route, Switch } from 'react-router-dom';
import MenuBuilder from './containers/Menu/Menu';
import Landing from './containers/Landing';
import { PrivacyTerms } from './containers/Landing/PrivacyTerms';
import { TermsAndConditions } from './containers/Landing/TermsAndConditions';
import { DeleteAccount } from './containers/Landing/DeleteAccount';
import { userIsAuthenticatedRedir, userIsNotAuthenticatedRedir } from './auth';
import asyncComponent from './hoc/asyncComponent/asyncComponent';
import * as liveChat from './shared/liveChat';
import SupportDock from './support/SupportDock';
import './App.scss';

const MenuComponent = userIsAuthenticatedRedir(MenuBuilder);
const LandingComponent = userIsNotAuthenticatedRedir(Landing);
const asyncSignUp = asyncComponent(() => {
  return import('./containers/SignUp/SignUp');
});

const asyncChangePassword = asyncComponent(() => {
  return import('./containers/Password/ChangePassword');
});

const asyncResetPassword = asyncComponent(() => {
  return import('./containers/Password/ResetPassword');
});

const asyncUnsubscribe = asyncComponent(() => {
  return import('./containers/Unsubscribe');
});

const asyncLinkVoucher = asyncComponent(() => {
  return import('./containers/LinkVoucher');
});

// Email sign-in links: https://new.lunch.team/MagicLogin/<token> (also ?token=).
// The page verifies only when the person clicks "Sign me in".
const asyncMagicLogin = asyncComponent(() => {
  return import('./containers/MagicLogin');
});

// Set when the support composer took over and hid the widget; a later mount (sign-out, or the
// composer failing to load) brings it back.
let liveChatHiddenForComposer = false;

class LiveChatLoader extends Component {
  componentDidMount() {
    document.documentElement.classList.remove('lunchteam-livechat-off');
    this.installPositionStyle();
    if (liveChatHiddenForComposer) {
      liveChatHiddenForComposer = false;
      this.showWidgetAgain();
    }

    if (this.isMobileSurface()) {
      document.documentElement.classList.add('lunchteam-mobile-surface');
      return;
    }

    if (window.LC_API || document.getElementById('lunchteam-livechat-script')) {
      this.installFallbackLauncher();
      this.startFallbackVisibilityChecks();
      return;
    }

    window.__lc = window.__lc || {};
    window.__lc.license = liveChat.LICENSE_ID;

    const script = document.createElement('script');
    script.id = 'lunchteam-livechat-script';
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.livechatinc.com/tracking.js';

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    this.installFallbackLauncher();
    this.startFallbackVisibilityChecks();
  }

  // Unmounted when the support composer takes over (customer signed in): hide LiveChat and its fallback button.
  componentWillUnmount() {
    if (this.liveChatFallbackInterval) {
      window.clearInterval(this.liveChatFallbackInterval);
    }
    (this.liveChatFallbackTimeouts || []).forEach(timeout => window.clearTimeout(timeout));
    const button = document.getElementById('lunchteam-livechat-fallback');
    if (button && button.parentNode) {
      button.parentNode.removeChild(button);
    }
    document.documentElement.classList.add('lunchteam-livechat-off');
    liveChatHiddenForComposer = true;
    try {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
        window.LiveChatWidget.call('hide');
      } else if (window.LC_API && typeof window.LC_API.hide_chat_window === 'function') {
        window.LC_API.hide_chat_window();
      }
    } catch (e) {
      // The CSS rule below still hides the widget.
    }
  }

  // Undoes the 'hide' above: the widget comes back minimised, as its launcher.
  showWidgetAgain() {
    try {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
        window.LiveChatWidget.call('minimize');
      } else if (window.LC_API && typeof window.LC_API.minimize_chat_window === 'function') {
        window.LC_API.minimize_chat_window();
      }
    } catch (e) {
      // The fallback launcher still opens the chat.
    }
  }

  installPositionStyle() {
    if (document.getElementById('lunchteam-livechat-position')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'lunchteam-livechat-position';
    style.textContent = [
      '#chat-widget-container{left:0!important;right:auto!important;}',
      '.lunchteam-livechat-off #chat-widget-container,.lunchteam-livechat-off #lunchteam-livechat-fallback{display:none!important;}',
      '.lunchteam-mobile-surface #chat-widget-container,',
      '.lunchteam-mobile-surface #lunchteam-livechat-fallback{display:none!important;visibility:hidden!important;pointer-events:none!important;}',
      '@media (max-width:767px){#chat-widget-container,#lunchteam-livechat-fallback{display:none!important;visibility:hidden!important;pointer-events:none!important;}}'
    ].join('');
    document.head.appendChild(style);
  }

  isMobileSurface() {
    const userAgent = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod/i.test(userAgent)
      || window.matchMedia('(max-width: 767px)').matches;
  }

  installFallbackLauncher() {
    if (document.getElementById('lunchteam-livechat-fallback')) {
      return;
    }

    const button = document.createElement('button');
    button.id = 'lunchteam-livechat-fallback';
    button.type = 'button';
    button.setAttribute('aria-label', 'Open Lunch.Team support chat');
    button.textContent = 'Chat with us';
    button.style.cssText = [
      'display:none',
      'position:fixed',
      'left:16px',
      'bottom:16px',
      'z-index:2147483647',
      'border:0',
      'border-radius:4px',
      'background:#0e7a4b',
      'color:#fff',
      'font:600 14px/1.2 Arial,sans-serif',
      'padding:14px 18px',
      'box-shadow:0 4px 16px rgba(0,0,0,0.28)',
      'cursor:pointer'
    ].join(';');
    button.addEventListener('click', this.openLiveChat);
    document.body.appendChild(button);
  }

  startFallbackVisibilityChecks() {
    this.updateFallbackLauncherVisibility();
    this.liveChatFallbackTimeouts = [
      window.setTimeout(this.updateFallbackLauncherVisibility, 2500),
      window.setTimeout(this.updateFallbackLauncherVisibility, 6000),
    ];
    this.liveChatFallbackInterval = window.setInterval(this.updateFallbackLauncherVisibility, 5000);
  }

  isNativeLiveChatVisible() {
    const container = document.getElementById('chat-widget-container');
    if (!container) {
      return false;
    }

    const style = window.getComputedStyle(container);
    const rect = container.getBoundingClientRect();
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && style.opacity !== '0'
      && style.zIndex !== '-1'
      && rect.width > 0
      && rect.height > 0;
  }

  updateFallbackLauncherVisibility = () => {
    const button = document.getElementById('lunchteam-livechat-fallback');
    if (!button) {
      return;
    }
    button.style.display = this.isNativeLiveChatVisible() ? 'none' : 'block';
  }

  openLiveChat = () => {
    if (window.LC_API && typeof window.LC_API.open_chat_window === 'function') {
      window.LC_API.open_chat_window();
      window.setTimeout(this.updateFallbackLauncherVisibility, 500);
      window.setTimeout(this.updateFallbackLauncherVisibility, 2000);
      return;
    }

    window.open('https://secure.livechatinc.com/licence/6817451/v2/open_chat.cgi?groups=1', '_blank', 'noopener,noreferrer');
  }

  render() {
    return null;
  }
}

class App extends Component {
  render() {
    return (
      <div>
        <Switch>
          <Route path="/Signup" component={asyncSignUp} />
          <Route path="/Unsubscribe" component={asyncUnsubscribe} />
          <Route path="/ChangePassword" component={asyncChangePassword} />
          <Route path="/ResetPassword" component={asyncResetPassword} />
          <Route path="/MagicLogin" component={asyncMagicLogin} />
          <Route path="/Menu" component={MenuComponent} />
          <Route path="/GetVoucher" component={asyncLinkVoucher} />
          <Route path="/" exact component={LandingComponent} />
          <Route path="/Privacy" component={PrivacyTerms} />
          <Route path="/Terms" component={TermsAndConditions} />
          <Route path="/delete-account" component={DeleteAccount} />
          <Route path="/DeleteAccount" component={DeleteAccount} />
        </Switch>
        <SupportDock fallback={<LiveChatLoader />} />
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js